import { useMemo, useState } from 'react';
import { ChooserDialog, ConfirmDialog } from '@core/components';
import type { Design } from '../../../../api/design';
import type { DesignPreset } from '../../../../api/designPreset';
import type { PastCombination } from '../../../../api/pastCombination';
import { PresetSummary } from '../../../components/PresetSummary';
import { useAppContext } from '../../../context/AppContext';
import {
  getDesignPresetSummary,
  getDesignPresetSummaryContext,
  getPresetFeatures,
} from '../../../utils/designPreset';
import { PastOrdersDrawer } from './PastOrdersDrawer';

interface DesignPresetChooserDialogProps {
  open: boolean;
  design: Design;
  presets: DesignPreset[];
  hasPastCombinations: boolean;
  onApplyValues: (values: DesignPreset | PastCombination) => void;
  onDismiss: () => void;
}

export function DesignPresetChooserDialog({
  open,
  design,
  presets,
  hasPastCombinations,
  onApplyValues,
  onDismiss,
}: DesignPresetChooserDialogProps) {
  const {
    vestments,
    fabrics,
    colors,
    threads,
    trimmings,
    galoons,
    cords,
    tassels,
  } = useAppContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const features = getPresetFeatures(design.vestmentType);
  const summaryCtx = useMemo(
    () =>
      getDesignPresetSummaryContext(design.vestmentType, {
        vestments,
        fabrics,
        colors,
        threads,
        trimmings,
        galoons,
        cords,
        tassels,
      }),
    [
      design.vestmentType,
      vestments,
      fabrics,
      colors,
      threads,
      trimmings,
      galoons,
      cords,
      tassels,
    ]
  );

  if (!open || !features) {
    return null;
  }

  const closeDrawer = () => setDrawerOpen(false);

  const handlePastApply = (combination: PastCombination) => {
    setDrawerOpen(false);
    onApplyValues(combination);
  };

  const drawer = (
    <PastOrdersDrawer
      open={drawerOpen}
      designId={design.id}
      designType={design.vestmentType}
      onApply={handlePastApply}
      onClose={closeDrawer}
    />
  );

  // Past combinations only: confirm, then drawer (Cancel returns to this confirm).
  if (presets.length === 0) {
    if (!hasPastCombinations) {
      return null;
    }
    return (
      <>
        <ConfirmDialog
          open={open && !drawerOpen}
          title="Do you want to use a past combination?"
          content={
            features.kind === 'chasuble'
              ? 'Reuse body, panel, and collar values from a previous order of this design.'
              : 'Reuse panel, cord, and tassel values from a previous order of this design.'
          }
          confirmText="Yes"
          cancelText="No"
          onConfirm={() => setDrawerOpen(true)}
          onCancel={onDismiss}
          maxWidth="xs"
        />
        {drawer}
      </>
    );
  }

  // Single named preset and no past combinations: keep the quick Yes/No confirm.
  if (presets.length === 1 && !hasPastCombinations) {
    const preset = presets[0];
    return (
      <ConfirmDialog
        open={open}
        title="Do you want to apply this preset?"
        content={
          <PresetSummary
            name={preset.name}
            segments={getDesignPresetSummary(preset, features, summaryCtx)}
            selected
          />
        }
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => onApplyValues(preset)}
        onCancel={onDismiss}
        maxWidth="xs"
      />
    );
  }

  return (
    <>
      <ChooserDialog
        open={open}
        onClose={onDismiss}
        title="Choose a preset"
        options={presets}
        getOptionKey={(preset) => preset.id}
        renderOption={(preset, selected) => (
          <PresetSummary
            name={preset.name}
            segments={getDesignPresetSummary(preset, features, summaryCtx)}
            selected={selected}
          />
        )}
        actionOption={
          hasPastCombinations
            ? {
                primary: 'From past orders',
                secondary: 'Reuse a combination from a previous order',
                onClick: () => setDrawerOpen(true),
              }
            : undefined
        }
        noneOption={{
          primary: 'No preset',
          secondary: 'Leave fields as they are',
        }}
        onSelect={onApplyValues}
        resetKey={design.id}
        maxWidth="xs"
      />
      {drawer}
    </>
  );
}
